import { SelectHTMLAttributes, forwardRef, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import Modal from '@/components/ui/Modal';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  labelInfoTooltip?: string;
  labelInfoModalTitle?: string;
  labelInfoModalContent?: React.ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      options,
      placeholder,
      labelInfoTooltip,
      labelInfoModalTitle,
      labelInfoModalContent,
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const [showInfo, setShowInfo] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className="form-label inline-flex items-center gap-2">
            <span>{label}</span>
            {props.required && <span className="text-red-500 ml-0.5">*</span>}
            {(labelInfoTooltip || labelInfoModalContent) && (
              <button
                type="button"
                className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 rounded"
                title={labelInfoTooltip}
                aria-label={(label ? label + ' ' : '') + 'info'}
                onClick={() => {
                  if (labelInfoModalContent) {
                    setShowInfoModal(true);
                  } else {
                    setShowInfo((v) => !v);
                  }
                }}
              >
                <Info size={14} />
              </button>
            )}
          </label>
        )}
        {labelInfoTooltip && !labelInfoModalContent && showInfo && (
          <div className="mt-1 mb-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-line">
            {labelInfoTooltip}
          </div>
        )}

        {labelInfoModalContent && (
          <Modal
            isOpen={showInfoModal}
            onClose={() => setShowInfoModal(false)}
            title={labelInfoModalTitle || label}
            size="md"
          >
            <div className="prose prose-sm max-w-none">
              {labelInfoModalContent}
            </div>
          </Modal>
        )}
        
        <div className="relative">
          <select
            id={selectId}
            ref={ref}
            className={cn(
              'form-input appearance-none pr-10 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700',
              error && 'form-input-error',
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>
        </div>
        
        {error && (
          <p className="form-error">
            {error}
          </p>
        )}
        
        {helperText && !error && (
          <p className="form-help">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
